import axios from 'axios';
import {
  PublicPortal,
  PublicPortalJsonForm,
  PublicPortalJsonFormControl,
  PublicPortalJsonFormField,
  PublicPortalManualTriggerInput,
  PublicPortalRun,
  PublicPortalRunCreateRequest,
  PublicPortalRunCreateResponse,
} from '@/pages/PublicPortal/types.ts';
import { AutomationSchemaRef } from '@/types/automationFields.ts';
import { ManualTriggerInputType } from '@/types/automationSteps.ts';

export const DEFAULT_PUBLIC_PORTAL_RUN_ERROR_MESSAGE =
  'This request could not be completed. Please contact the team that shared this portal.';

export const publicPortalApi = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v0',
  headers: { 'Content-Type': 'application/json' },
});

export const retrievePublicPortal = async (portalIdentifier: string): Promise<PublicPortal> => {
  const response = await publicPortalApi.get<PublicPortal>(`/portals/${portalIdentifier}`);
  if (!isPublicPortal(response.data)) {
    throw new Error('This portal is not available.');
  }
  return {
    ...response.data,
    run_error_message: response.data.run_error_message || DEFAULT_PUBLIC_PORTAL_RUN_ERROR_MESSAGE,
  };
};

export const createPublicPortalRun = async (
  portalIdentifier: string,
  payload: PublicPortalRunCreateRequest,
): Promise<PublicPortalRunCreateResponse> => {
  const response = await publicPortalApi.post<PublicPortalRunCreateResponse>(
    `/portals/${portalIdentifier}/runs`,
    payload,
  );
  return response.data;
};

export const processPublicPortalRunInputs = async (
  portalIdentifier: string,
  publicRunToken: string,
): Promise<PublicPortalRun> => {
  const response = await publicPortalApi.put<PublicPortalRun>(
    `/portals/${portalIdentifier}/runs/${publicRunToken}/inputs/process`,
    {},
  );
  return response.data;
};

export const retrievePublicPortalRun = async (
  portalIdentifier: string,
  publicRunToken: string,
): Promise<PublicPortalRun> => {
  const response = await publicPortalApi.get<PublicPortalRun>(
    `/portals/${portalIdentifier}/runs/${publicRunToken}`,
  );
  return response.data;
};

function isPublicPortal(value: unknown): value is PublicPortal {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<PublicPortal>;
  return (
    typeof candidate.selection_token === 'string' &&
    candidate.selection_token.length > 0 &&
    (typeof candidate.public_slug === 'string' ||
      candidate.public_slug === null ||
      typeof candidate.public_slug === 'undefined') &&
    typeof candidate.title === 'string' &&
    typeof candidate.description === 'string' &&
    (typeof candidate.run_error_message === 'string' ||
      typeof candidate.run_error_message === 'undefined') &&
    (typeof candidate.logo_url === 'string' || candidate.logo_url === null) &&
    Array.isArray(candidate.manual_trigger_inputs) &&
    candidate.manual_trigger_inputs.every(isPublicPortalManualTriggerInput)
  );
}

function isPublicPortalManualTriggerInput(value: unknown): value is PublicPortalManualTriggerInput {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<PublicPortalManualTriggerInput>;
  const hasBaseShape =
    typeof candidate.output_id === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.description === 'string' &&
    typeof candidate.is_required === 'boolean' &&
    (candidate.input_type === ManualTriggerInputType.File ||
      candidate.input_type === ManualTriggerInputType.Folder ||
      candidate.input_type === ManualTriggerInputType.Text ||
      candidate.input_type === ManualTriggerInputType.Json);
  if (!hasBaseShape) return false;
  return (
    candidate.input_type !== ManualTriggerInputType.Json ||
    (isAutomationSchemaRef(candidate.schema_ref) && isPublicPortalJsonForm(candidate.portal_form))
  );
}

function isAutomationSchemaRef(value: unknown): value is AutomationSchemaRef {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AutomationSchemaRef>;
  return (
    typeof candidate.schema_id === 'string' &&
    candidate.schema_id.length > 0 &&
    typeof candidate.version === 'number' &&
    Number.isInteger(candidate.version) &&
    candidate.version >= 1
  );
}

function isPublicPortalJsonForm(value: unknown): value is PublicPortalJsonForm {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PublicPortalJsonForm>;
  return (
    typeof candidate.title === 'string' &&
    typeof candidate.description === 'string' &&
    Array.isArray(candidate.fields) &&
    candidate.fields.length > 0 &&
    candidate.fields.every(isPublicPortalJsonFormField)
  );
}

function isPublicPortalJsonFormField(value: unknown): value is PublicPortalJsonFormField {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PublicPortalJsonFormField>;
  return (
    Array.isArray(candidate.path) &&
    candidate.path.length > 0 &&
    candidate.path.every((segment) => typeof segment === 'string' && segment.length > 0) &&
    typeof candidate.label === 'string' &&
    typeof candidate.help_text === 'string' &&
    typeof candidate.placeholder === 'string' &&
    typeof candidate.group === 'string' &&
    typeof candidate.is_required === 'boolean' &&
    isPublicPortalJsonFormControl(candidate.control) &&
    !candidate.path.some(isUnsafeObjectPathSegment) &&
    (candidate.enum_values === null ||
      typeof candidate.enum_values === 'undefined' ||
      (Array.isArray(candidate.enum_values) &&
        candidate.enum_values.every((entry) => typeof entry === 'string'))) &&
    isOptionalFiniteNumber(candidate.minimum) &&
    isOptionalFiniteNumber(candidate.maximum) &&
    isOptionalNonNegativeInteger(candidate.min_length) &&
    isOptionalNonNegativeInteger(candidate.max_length)
  );
}

function isPublicPortalJsonFormControl(value: unknown): value is PublicPortalJsonFormControl {
  return (
    value === 'text' ||
    value === 'textarea' ||
    value === 'number' ||
    value === 'integer' ||
    value === 'boolean' ||
    value === 'select' ||
    value === 'date' ||
    value === 'email' ||
    value === 'url'
  );
}

function isOptionalFiniteNumber(value: unknown): boolean {
  return (
    value === null ||
    typeof value === 'undefined' ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

function isOptionalNonNegativeInteger(value: unknown): boolean {
  return (
    value === null ||
    typeof value === 'undefined' ||
    (typeof value === 'number' && Number.isInteger(value) && value >= 0)
  );
}

function isUnsafeObjectPathSegment(value: string): boolean {
  return value === '__proto__' || value === 'constructor' || value === 'prototype';
}
